import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    toggleSidebar,
    setSidebarOpen,
    setCurrentPage,
    setLoading,
    addNotification,
    removeNotification,
    clearNotifications,
    setTheme,
    setBreatheMode,
    startFlowState,
    stopFlowState,
    resetFlowState,
    triggerAnimation,
    stopAnimation,
    setShowHeader,
    setShowAIAgent,
} from '../store/slices/uiSlice';
import { createDynamicTheme } from '@/themes';

interface NotificationPayload {
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
    duration?: number;
}

export const useUI = () => {
    const dispatch = useAppDispatch();
    const {
        sidebarOpen,
        currentPage,
        isLoading,
        notifications,
        theme,
        breatheMode,
        flowState,
        animations,
        showHeader,
        showAIAgent,
    } = useAppSelector((state) => state.ui);

    const themePallete = useMemo(() => {
        return createDynamicTheme('', theme);
    }, []);

    // Sidebar actions
    const handleToggleSidebar = useCallback(() => {
        dispatch(toggleSidebar());
    }, [dispatch]);

    const handleSetSidebarOpen = useCallback(
        (open: boolean) => {
            dispatch(setSidebarOpen(open));
        },
        [dispatch],
    );

    const handleShowAIAgent = useCallback((show: boolean) => {
        dispatch(setShowAIAgent(show));
    }, []);

    // Page navigation
    const handleSetCurrentPage = useCallback(
        (page: string) => {
            dispatch(setCurrentPage(page));
        },
        [dispatch],
    );

    // Loading state
    const handleSetLoading = useCallback(
        (loading: boolean) => {
            dispatch(setLoading(loading));
        },
        [dispatch],
    );

    // Notifications
    const showNotification = useCallback(
        (notification: NotificationPayload) => {
            dispatch(addNotification(notification));

            // Auto-remove notification after duration
            if (notification.duration !== 0) {
                setTimeout(() => {
                    // We'll need to get the notification ID somehow
                    // For now, we'll clear all notifications after duration
                }, notification.duration || 5000);
            }
        },
        [dispatch],
    );

    const showSuccess = useCallback(
        (title: string, message: string, duration?: number) => {
            showNotification({ type: 'success', title, message, duration });
        },
        [showNotification],
    );

    const showError = useCallback(
        (title: string, message: string, duration?: number) => {
            showNotification({ type: 'error', title, message, duration });
        },
        [showNotification],
    );

    const showWarning = useCallback(
        (title: string, message: string, duration?: number) => {
            showNotification({ type: 'warning', title, message, duration });
        },
        [showNotification],
    );

    const showInfo = useCallback(
        (title: string, message: string, duration?: number) => {
            showNotification({ type: 'info', title, message, duration });
        },
        [showNotification],
    );

    const hideNotification = useCallback(
        (id: string) => {
            dispatch(removeNotification(id));
        },
        [dispatch],
    );

    const clearAllNotifications = useCallback(() => {
        dispatch(clearNotifications());
    }, [dispatch]);

    // Theme
    const handleSetTheme = useCallback(
        (newTheme: 'light' | 'dark') => {
            dispatch(setTheme(newTheme));
        },
        [dispatch],
    );

    const toggleTheme = useCallback(() => {
        dispatch(setTheme(theme === 'light' ? 'dark' : 'light'));
    }, [dispatch, theme]);

    // Breathe Mode
    const handleSetBreatheMode = useCallback(
        (enabled: boolean) => {
            dispatch(setBreatheMode(enabled));
        },
        [dispatch],
    );

    const toggleBreatheMode = useCallback(() => {
        dispatch(setBreatheMode(!breatheMode));
    }, [dispatch, breatheMode]);

    // Flow State
    const handleStartFlowState = useCallback(() => {
        dispatch(startFlowState());
    }, [dispatch]);

    const handleEndFlowState = useCallback(() => {
        dispatch(stopFlowState());
    }, [dispatch]);

    const handleResetFlowState = useCallback(() => {
        dispatch(resetFlowState());
    }, [dispatch]);

    // Animations
    const playAnimation = useCallback(
        (animationType: 'confetti' | 'celebration' | 'levelUp') => {
            dispatch(triggerAnimation(animationType));
        },
        [dispatch],
    );

    const stopAnimationEffect = useCallback(
        (animationType: 'confetti' | 'celebration' | 'levelUp') => {
            dispatch(stopAnimation(animationType));
        },
        [dispatch],
    );

    const playConfetti = useCallback(() => {
        playAnimation('confetti');
    }, [playAnimation]);

    const playCelebration = useCallback(() => {
        playAnimation('celebration');
    }, [playAnimation]);

    const playLevelUp = useCallback(() => {
        playAnimation('levelUp');
    }, [playAnimation]);

    const handleSetShowHeader = useCallback(
        (show: boolean) => {
            dispatch(setShowHeader(show));
        },
        [dispatch],
    );

    return {
        // State
        sidebarOpen,
        currentPage,
        isLoading,
        notifications,
        theme,
        themePallete,
        breatheMode,
        flowState,
        animations,
        showHeader,
        showAIAgent,
        // Sidebar Actions
        toggleSidebar: handleToggleSidebar,
        setSidebarOpen: handleSetSidebarOpen,
        setShowAIAgent: handleShowAIAgent,
        // Page Actions
        setCurrentPage: handleSetCurrentPage,

        // Loading Actions
        setLoading: handleSetLoading,

        // Notification Actions
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hideNotification,
        clearAllNotifications,

        // Theme Actions
        setTheme: handleSetTheme,
        toggleTheme,

        // Breathe Mode Actions
        setBreatheMode: handleSetBreatheMode,
        toggleBreatheMode,

        // Flow State Actions
        startFlowState: handleStartFlowState,
        endFlowState: handleEndFlowState,
        resetFlowState: handleResetFlowState,

        // Animation Actions
        playAnimation,
        stopAnimation: stopAnimationEffect,
        playConfetti,
        playCelebration,
        playLevelUp,

        // Header Actions
        setShowHeader: handleSetShowHeader,
    };
};
